import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { RoleAssignmentSummary, RoleAssignmentSummaryStore } from 'src/stores/role-assignments-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Row, Select, Spin, Tag, Tooltip } from 'antd';
import { ListState, Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { clone } from 'src/core/utils/object';
import NewRoleAssignmentDialog from './license-role-assignment-new';
import { SearchOutlined } from '@ant-design/icons';
import { DateRangeFilter } from '../../common/components/table-filters/date-range-filter';
import { isNullOrUndefined } from 'util';
import moment from 'moment';
import { LicenseCreationAttributesStore } from '../../stores/licensing-store';
import { useCurrentUserTenantPermissionsFilter } from '../../common/permission-hooks';
import { PermissionsConstants } from '../../common/types';
import { useIdentity } from 'src/core/services/authentication';
import { LicenseManagerRoleName } from '../../stores/roles-store';
import UpdateRoleAttributesDialog from 'src/pages/role-assignments/role-attributes/update-role-attributes';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { ValidatedSearch } from 'src/common/components/validated-search';

const permissions = [PermissionsConstants.ManageGlobalLicenses];
const RoleAssignmentsListPage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'identityTitle', direction: 'Ascending', useProfile: false }],
    filter: [],
    skip: 0,
    take: 10,
  } as Query);

  const RoleAssignmentsStore = container.get(RoleAssignmentSummaryStore);
  const RoleAssignmentsState = RoleAssignmentsStore.state;
  const [newRoleAssignmentVisible, setNewRoleAssignmentVisible] = useState(false);
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasGlobalLicensemanagerPermission = userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses);
  const [licenseAttrVisible, setLicenseAttrVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<RoleAssignmentSummary>();
  const [selectedRowsLength, setSelectedRowsLength] = useState<number>(0);
  const [identityFilter, setIdentityFilter] = useState('');
  const [entityFilter, setScopeFilter] = useState('');

  setHeader(t('RoleAssignments'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'role-assignments', breadcrumbName: t('RoleAssignments') },
  ]);

  useEffect(
    () => {
      RoleAssignmentsStore.loadLicenseAssignments(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const expirationDateFilter = (query.filter as any)['roleExpirationDate'];
  const hasExpirationDateFilter =
    !isNullOrUndefined(expirationDateFilter) && (!isNullOrUndefined(expirationDateFilter.ge) || !isNullOrUndefined(expirationDateFilter.le));
  const expirationDateHeader = t('ExpirationDate') + ' ' + (hasExpirationDateFilter ? '[*]' : '');

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={RoleAssignmentsStore.baseUrl} fieldName={'identityTitle'} fieldValue={'identityTitle'} />,
        fiterField: 'identityTitle',
        field: 'identityTitle',
        title: t('Identity'),
        renderer: (data) => <span>{data.identityTitle}</span>,
      },
      {
        sortable: true,
        searchable: false,
        filter: (
          <Select placeholder={t('Select Type')} style={{ width: '100%' }} allowClear showSearch>
            <Select.Option key="User" value="User">
              {t('User')}
            </Select.Option>
            <Select.Option key="Group" value="Group">
              {t('Group')}
            </Select.Option>
          </Select>
        ),
        fiterField: 'identityType',
        field: 'identityType',
        title: t('IdentityType'),
        renderer: (data) => (
          <span>
            <Tag>{data.identityType}</Tag>
          </span>
        ),
      },
      {
        field: 'Role',
        sortable: true,
        sortField: 'Role',
        useProfile: true,
        searchable: true,
        fiterField: 'Role/Name',
        filter: (
          <OptionsFilter
            options={[
              { label: t('License Manager'), value: 'License Manager' },
              { label: t('Global License Manager'), value: 'Global License Manager' },
            ]}
          />
        ),
        title: t('Role'),
        renderer: (data) => <span>{data.role}</span>,
      },
      {
        field: 'ScopeTitle',
        sortable: true,
        sortField: 'ScopeTitle',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={RoleAssignmentsStore.baseUrl} fieldName={'Scope/Title'} fieldValue={'scopeTitle'} />,
        fiterField: 'Scope/Title',
        title: t('Scope'),
        renderer: (data) => <span>{data.scopeTitle}</span>,
      },
      {
        field: 'roleExpirationDate',
        sortable: true,
        searchable: false,
        title: t(expirationDateHeader),
        filter: <DateRangeFilter />,
        renderer: (data) => <span>{data.expirationDate ? moment(data.expirationDate).format(getDateFormat()) : ''}</span>,
      },
    ],
    data: RoleAssignmentsState.value as ListState<RoleAssignmentSummary>,
    sortFields: [],
  } as TableModel<RoleAssignmentSummary>;

  const getFilterArray = (filterData: any) => {
    if (filterData == null) return [] as any[];
    if (filterData instanceof Array || Array.isArray(filterData)) return filterData as any[];

    let rawData: string[] = [];
    for (const [key, value] of Object.entries(filterData)) {
      const newItem: string = isNaN(key as any) ? getItemQuery(key, value) : `${value}`;
      rawData = rawData.filter((x) => x.split(' ')[0] !== newItem.split(' ')[0]);
      if (value) rawData.push(newItem);
    }

    return rawData;
  };

  const getItemQuery = (key: string, value: any) => {
    if (key === 'roleExpirationDate') {
      return `${key} ge ${value.ge.value} and ${key} le ${value.le.value}`;
    }
    return `${key} eq '${value}'`;
  };

  const onScopeSearchFilterChanged = (value: string) => {
    const nextQuery = clone<Query>(query);

    const nextQueryfilter = getFilterArray(nextQuery.filter).filter((x) => x !== entityFilter);

    let nextFilter = (null as unknown) as string;
    if (value?.length !== 0) nextFilter = `contains(tolower(Scope/Title), '${value.toLowerCase()}')`;

    if (nextFilter != null) nextQuery.filter = [...nextQueryfilter, nextFilter];
    else nextQuery.filter = nextQueryfilter;

    setQuery(nextQuery);
    setScopeFilter(nextFilter);
    RoleAssignmentsStore.loadLicenseAssignments(nextQuery);
  };

  const onIdentitySearchFilterChanged = (value: string) => {
    const nextQuery = clone<Query>(query);

    const nextQueryfilter = getFilterArray(nextQuery.filter).filter((x) => x !== identityFilter);

    let nextFilter = (null as unknown) as string;
    if (value?.length !== 0) nextFilter = `contains(tolower(IdentityTitle), '${value.toLowerCase()}')`;

    if (nextFilter != null) nextQuery.filter = [...nextQueryfilter, nextFilter];
    else nextQuery.filter = nextQueryfilter;

    setQuery(nextQuery);
    setIdentityFilter(nextFilter);
    RoleAssignmentsStore.loadLicenseAssignments(nextQuery);
  };
  const onSelectionChanged = (rowsKeys: any[], items: RoleAssignmentSummary[]) => {
    if (rowsKeys.length === 1) {
      var assignment = items.firstOrDefault();
      if (assignment) {
        setSelectedAssignment(assignment as RoleAssignmentSummary);
        return;
      }
    }
    setSelectedRowsLength(rowsKeys.length);
    setSelectedAssignment(undefined);
  };

  const includeSearches = (q: Query) => {
    const nextQuery = clone<Query>(q);

    const nextQueryfilter = getFilterArray(nextQuery.filter).filter((x) => x !== entityFilter && x !== identityFilter);

    let nextFiters = [];
    if (entityFilter?.length !== 0) nextFiters.push(entityFilter);
    if (identityFilter?.length !== 0) nextFiters.push(identityFilter);

    nextQuery.filter = [...nextQueryfilter, ...nextFiters];
    return nextQuery;
  };

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <Spin spinning={checkingPermissions || RoleAssignmentsState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}>
          <TableView
            rowKey="id"
            canCreateNew
            onDeleteRow={async (item: RoleAssignmentSummary) => {
              return await RoleAssignmentsStore.deleteLicenseAssignment(item.id);
            }}
            onNewItem={() => setNewRoleAssignmentVisible(true)}
            newItemName={t('AddAssignments')}
            canDelete={true}
            canEdit={false}
            onSelectionChanged={onSelectionChanged}
            editLabel={t('EditSelected')}
            canSort
            onQueryChanged={(query: Query) => {
              const augmentedQuery = includeSearches(query);
              setQuery(augmentedQuery);
              RoleAssignmentsStore.loadLicenseAssignments(augmentedQuery);
            }}
            onRefresh={() => {
              RoleAssignmentsStore.loadLicenseAssignments(query);
              setSelectedAssignment(undefined);
            }}
            model={RoleAssignmentsState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              RoleAssignmentsState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(RoleAssignmentsState.errorMessage.value) : t('RoleAssignmentsTableError'))
            }
            rightToolbar={
              <>
                <li hidden={selectedRowsLength > 0}>
                  <ValidatedSearch
                    allowClear
                    placeholder={t('SearchScope')}
                    prefix={<SearchOutlined />}
                    style={{ width: '100%', minWidth: 150 }}
                    onPressEnter={(search: any) => onScopeSearchFilterChanged(search.target.value)}
                  />
                </li>
                <li hidden={selectedRowsLength > 0} style={{ marginRight: 5 }}>
                  <ValidatedSearch
                    allowClear
                    placeholder={t('SearchIdentity')}
                    prefix={<SearchOutlined />}
                    style={{ width: '100%', minWidth: 150 }}
                    onPressEnter={(search: any) => onIdentitySearchFilterChanged(search.target.value)}
                  />
                </li>
                <li hidden={!selectedAssignment}>
                  <Tooltip placement="topLeft" title={t('UpdateRoleAttributes')}>
                    <Button type="primary" style={{ width: 'auto', minWidth: 'max-content' }} onClick={() => setLicenseAttrVisible(true)}>
                      {t('UpdateRoleAttributes')}
                    </Button>
                  </Tooltip>
                </li>
              </>
            }
          />
          {newRoleAssignmentVisible && (
            <NewRoleAssignmentDialog
              onClose={(response: any) => {
                setNewRoleAssignmentVisible(false);
                if (response) {
                  RoleAssignmentsStore.loadLicenseAssignments(query);
                }
              }}
            />
          )}
          {licenseAttrVisible && selectedAssignment && (
            <UpdateRoleAttributesDialog
              editLicenseManagerRoleAttributes={hasGlobalLicensemanagerPermission && selectedAssignment.role === LicenseManagerRoleName}
              identityType={selectedAssignment.identityType}
              scopeId={selectedAssignment.scopeId}
              identityId={selectedAssignment.identityId}
              onClose={(newExpirationDate?: Date) => {
                if (newExpirationDate && selectedAssignment.expirationDate !== newExpirationDate) {
                  RoleAssignmentsStore.updateExpiration(selectedAssignment.id, {
                    scopeId: selectedAssignment.scopeId,
                    newExpirationDate: newExpirationDate,
                  }).then(() => {
                    RoleAssignmentsStore.loadLicenseAssignments(query);
                  });
                }
                setLicenseAttrVisible(false);
              }}
              roleExpirationDate={selectedAssignment.expirationDate}
            />
          )}
        </Spin>
      </Card>
    </Row>
  );
};

export default RoleAssignmentsListPage;
